import {
  createContext,
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";

export const NotificationVariant = {
  error: "error",
  info: "info",
  success: "success",
  warning: "warning",
} as const;

interface Notification {
  id?: number;
  message: string | React.ReactNode;
  variant: (typeof NotificationVariant)[keyof typeof NotificationVariant];
  duration?: number;
  persistent?: boolean;
}

const defaultApi = {
  notifications: [] as Notification[],
  setNotification: (notification: Notification) => null,
  clearNotification: (id: number) => null,
};

export type NotificationsContextValue = typeof defaultApi;

/**
 * Create Context
 */
export const NotificationsContext =
  createContext<NotificationsContextValue>(defaultApi);

/**
 * Custom Notifications Provider
 */
export function NotificationsProvider({ children }: any) {
  const [notifications, setNotifications] = useState<Notification[]>(
    defaultApi.notifications,
  );
  const timeoutRefs = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const location = useLocation();

  // Clear persistent notifications on route change
  useEffect(() => {
    setNotifications((prev) => {
      const persistentNotifications = prev.filter((n) => n.persistent);
      const nonPersistentNotifications = prev.filter((n) => !n.persistent);

      // Clear timeouts for persistent notifications that are being removed
      persistentNotifications.forEach((notification) => {
        if (notification.id) {
          const timeout = timeoutRefs.current.get(notification.id);
          if (timeout) {
            clearTimeout(timeout);
            timeoutRefs.current.delete(notification.id);
          }
        }
      });

      // Return only non-persistent notifications (persistent ones are removed)
      return nonPersistentNotifications;
    });
  }, [location.pathname]);

  // Method to push a new notification
  const setNotification = useCallback(
    (notification: Notification) => {
      // Clear any existing timeouts for previous notifications
      timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
      timeoutRefs.current.clear();

      const notificationId = Date.now();
      const nextNotifications = [
        {
          id: notificationId,
          ...notification,
        } as Notification,
      ];
      setNotifications(nextNotifications);

      // Set up auto-clear timeout if notification is not persistent
      // Default to 5 seconds (5000ms) if no duration is provided
      if (!notification.persistent) {
        const duration = notification.duration ?? 5000;
        const timeout = setTimeout(() => {
          setNotifications((prev) =>
            prev.filter((n) => n.id !== notificationId),
          );
          timeoutRefs.current.delete(notificationId);
        }, duration);
        timeoutRefs.current.set(notificationId, timeout);
      }
    },
    [setNotifications],
  );

  // Method to clear a notification
  const clearNotification = useCallback(
    (id: number) => {
      // Clear timeout if it exists
      const timeout = timeoutRefs.current.get(id);
      if (timeout) {
        clearTimeout(timeout);
        timeoutRefs.current.delete(id);
      }

      setNotifications((prev) => prev.filter((n) => n.id !== id));
    },
    [setNotifications],
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
      timeoutRefs.current.clear();
    };
  }, []);

  // Return Provider with full API
  const api = { notifications, setNotification, clearNotification };
  return (
    <NotificationsContext.Provider value={api}>
      {children}
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => {
  return useContext(NotificationsContext);
};
